// Painel executivo (diretoria) do Mapa de Ativas — card flutuante no lado
// direito. Consome /api/mapa/executivo: reporte de CAMPANHA (período de
// objetivos) com realizado acumulado × meta, comparativo com o mesmo período do
// ano anterior (semana Tupperware), ranking de distritos → grupos por % da meta,
// e timeline semanal. Oracle fail-soft: se cair, o backend devolve só o
// realizado da janela (MySQL) e o painel mostra o que tem. (2026-07-14)
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  FiTrendingUp, FiTrendingDown, FiRefreshCw, FiChevronDown, FiChevronRight,
  FiX, FiAward, FiTarget, FiBarChart2, FiChevronLeft, FiEye, FiEyeOff,
} from 'react-icons/fi'

type Fetch = (url: string, options?: RequestInit) => Promise<Response>

interface Grupo {
  codigo: string; nome: string; realizado: number; ativas: number
  meta: number | null; pct: number | null; anoAnterior: number; deltaYoY: number | null
}
interface Distrito extends Omit<Grupo, 'codigo' | 'nome'> {
  codigo: string; nome: string; grupos: Grupo[]
}
interface ExecData {
  oracle: { ok: boolean; motivo?: string }
  modo: 'semana' | 'campanha' | 'vitrine'
  semana: string | null
  vitrine?: {
    periodo: number; numero: number; semanaInicial: string; semanaFinal: string
    semanaFechamento: number; semanasDecorridas: number; totalSemanas: number; emCurso: boolean
  } | null
  campanha: {
    semanaInicial: string; semanaFinal: string; distritos: number
    // Relógio da campanha (só o modo campanha usa). `semanasDecorridas` inclui a
    // semana corrente, que ainda está aberta; `semanasRestantes` conta só as que
    // nem começaram.
    semanasTotal?: number; semanasDecorridas?: number | null; semanasRestantes?: number | null
  } | null
  headline: { realizado: number; meta: number | null; pct: number | null; anoAnterior: number; deltaYoY: number | null; anterior?: number | null; deltaPrev?: number | null; ativas: number } | null
  resumo: { distritosTotal: number; distritosNaMeta: number; gruposComMeta: number; gruposNaMeta: number } | null
  timeline: Array<{ semana: string; semanaCodigo: number; realizado: number; ativas: number }>
  vitrines?: Array<{ periodo: number; numero: number; total: number; fechamento: number; emCurso: boolean }>
  distritos: Distrito[]
  cached?: boolean
}

type Escopo = 'semana' | 'vitrine' | 'campanha'

const brl = (v: number, curto = false) => {
  if (curto) {
    if (Math.abs(v) >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`
    if (Math.abs(v) >= 1e3) return `R$ ${(v / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`
  }
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}
const semanaLabel = (s: string) => s?.replace('2026-W', 'S').replace(/^\d{4}-W/, 'S') || s

// Valor monetário com máscara de privacidade: por padrão os R$ vêm "quadriculados"
// (borrados, não-selecionáveis) pra não vazar números pra olhos curiosos; o olho
// no header revela. Só os R$ são mascarados — %, contagens e nomes ficam visíveis.
function Money({ v, curto = false, mask }: { v: number; curto?: boolean; mask: boolean }) {
  const txt = brl(v, curto)
  if (!mask) return <>{txt}</>
  return <span className="inline-block blur-[5px] select-none opacity-80" aria-label="valor oculto">{txt}</span>
}

// Cor por atingimento da meta.
const pctColor = (pct: number | null) =>
  pct == null ? 'text-muted-foreground'
    : pct >= 1 ? 'text-emerald-600 dark:text-emerald-400'
    : pct >= 0.8 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-500'
const barColor = (pct: number | null) =>
  pct == null ? 'bg-muted-foreground/40'
    : pct >= 1 ? 'bg-emerald-500'
    : pct >= 0.8 ? 'bg-amber-500'
    : 'bg-red-500'

function MetaBar({ pct }: { pct: number | null }) {
  if (pct == null) return null // modo semana não tem meta — sem barra vazia
  const w = Math.min(pct, 1.5) / 1.5 * 100
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden relative">
      <div className={`h-full rounded-full ${barColor(pct)} transition-all`} style={{ width: `${w}%` }} />
      {/* marca dos 100% */}
      <div className="absolute top-0 bottom-0 w-px bg-foreground/30" style={{ left: `${100 / 1.5}%` }} />
    </div>
  )
}

function YoY({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-muted-foreground/60">—</span>
  const up = delta >= 0
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
      {up ? <FiTrendingUp className="w-3 h-3" /> : <FiTrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{(delta * 100).toFixed(1)}%
    </span>
  )
}

function Sparkline({ data, mask }: { data: Array<{ semana: string; realizado: number }>; mask: boolean }) {
  if (data.length < 2) return null
  const max = Math.max(...data.map(d => d.realizado), 1)
  return (
    <div className="flex items-end gap-[2px] h-10">
      {data.map((d, i) => (
        <div key={i} className="flex-1 min-w-[3px] bg-primary/70 hover:bg-primary rounded-sm transition-colors"
          style={{ height: `${Math.max(d.realizado / max * 100, 3)}%` }}
          title={mask ? semanaLabel(d.semana) : `${semanaLabel(d.semana)} · ${brl(d.realizado, true)}`} />
      ))}
    </div>
  )
}

interface Props {
  fetchApi: Fetch
  onFocusDistrito?: (grupoCodigos: string[]) => void
  collapsedByDetail?: boolean
  semanaCodigo?: number | null
  // UF filtrada no mapa, quando houver.
  regiaoFiltrada?: string | null
  // Distritos que pertencem a essa UF (vêm do próprio dataset de ativas do mapa,
  // sem tabela nova). Cada distrito comercial é de um Estado só — conferido no
  // banco: 24/24 distritos e 349/349 grupos não cruzam UF —, então dá pra
  // recortar por SOMA o que o backend já entrega por distrito. Ver `vista`.
  distritosDaRegiao?: string[] | null
}

export function ExecutivoPanel({ fetchApi, collapsedByDetail, semanaCodigo, regiaoFiltrada, distritosDaRegiao }: Props) {
  const [data, setData] = useState<ExecData | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState(true)
  const [expandido, setExpandido] = useState<Set<string>>(new Set())
  // Escopo do reporte: Semana (chip clicado) · Vitrine (período comercial de 4
  // semanas — SPERIODO do DRACO) · Campanha (período de objetivos × meta).
  // Inicial derivado do chip pra não disparar fetch duplo no mount.
  const [escopo, setEscopo] = useState<Escopo>(() => semanaCodigo ? 'semana' : 'campanha')
  // Máscara de privacidade: valores em R$ começam OCULTOS (quadriculados) —
  // clique no olho pra revelar. Evita vazar faturamento pra olhos curiosos.
  const [mostrarValores, setMostrarValores] = useState(false)
  const mask = !mostrarValores

  // Acoplamento com os chips do mapa: clicar numa semana entra no escopo Semana
  // (a menos que o usuário esteja em Vitrine — aí mostra a vitrine daquela
  // semana); limpar pra "Todas" sai de Semana pra Campanha.
  useEffect(() => {
    setEscopo(prev => semanaCodigo
      ? (prev === 'campanha' ? 'semana' : prev)
      : (prev === 'semana' ? 'campanha' : prev))
  }, [semanaCodigo])

  // Trocar de aba dispara uma requisição nova sem cancelar a anterior, e elas não
  // voltam na ordem em que saíram — a vitrine chegou a demorar 6x mais que a
  // semana. Sem guarda, clicar Vitrine → Semana deixava a resposta da vitrine
  // chegar por último e sobrescrever a tela, com a aba Semana acesa. Só a
  // requisição mais recente pode escrever no estado.
  const reqSeq = useRef(0)

  const carregar = useCallback(async (refresh = false) => {
    const seq = ++reqSeq.current
    setLoading(true); setErro(null)
    try {
      const qs = new URLSearchParams()
      if (escopo === 'vitrine') qs.set('modo', 'vitrine')
      if (semanaCodigo && escopo !== 'campanha') qs.set('semana', String(semanaCodigo))
      if (refresh) qs.set('refresh', '1')
      const r = await fetchApi(`/api/mapa/executivo${qs.toString() ? `?${qs}` : ''}`)
      if (!r.ok) {
        // O backend manda {error} com o motivo (timeout do Oracle, período sem
        // objetivos…). Mostrar só "HTTP 500" escondia a causa na tela da diretoria.
        const motivo = await r.json().then((j: { error?: string }) => j?.error).catch(() => null)
        throw new Error(motivo || `HTTP ${r.status}`)
      }
      const json = await r.json()
      if (seq !== reqSeq.current) return
      setData(json)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setErro(e instanceof Error ? e.message : 'Falha ao carregar')
    } finally { if (seq === reqSeq.current) setLoading(false) }
  }, [fetchApi, semanaCodigo, escopo])

  useEffect(() => { carregar() }, [carregar])

  // ─── Recorte por Estado, por soma ──────────────────────────────────────────
  // O backend entrega `distritos[]` com realizado/meta/ativas/anoAnterior e os
  // grupos aninhados; headline e resumo são reduces sobre esse mesmo array
  // (server.cjs). Recortando a lista e refazendo os MESMOS reduces, o número do
  // Estado é exato — não é estimativa. Confirmado que Σ meta dos distritos bate
  // com o META_TOTAL da campanha.
  //
  // O que NÃO dá pra recortar aqui: `timeline`, `vitrines` e `anterior/deltaPrev`
  // saem de PEDIDOS sem join em GRUPOS, então não têm dimensão de distrito. Esses
  // blocos seguem sendo da praça inteira e são rotulados como tal na tela.
  const vista = useMemo(() => {
    if (!data) return null
    if (!regiaoFiltrada || !distritosDaRegiao || distritosDaRegiao.length === 0) return data
    const doRecorte = new Set(distritosDaRegiao)
    const distritos = data.distritos.filter(d => doRecorte.has(d.codigo))
    if (distritos.length === 0) return data

    const realizado = distritos.reduce((s, d) => s + d.realizado, 0)
    const metas = distritos.map(d => d.meta).filter((m): m is number => m != null)
    const meta = metas.length ? metas.reduce((s, m) => s + m, 0) : null
    const anoAnterior = distritos.reduce((s, d) => s + d.anoAnterior, 0)
    const ativas = distritos.reduce((s, d) => s + d.ativas, 0)
    const grupos = distritos.flatMap(d => d.grupos)
    const gruposComMeta = grupos.filter(g => g.meta)

    return {
      ...data,
      distritos,
      headline: data.headline && {
        ...data.headline,
        realizado, meta, pct: meta ? realizado / meta : null,
        anoAnterior, deltaYoY: anoAnterior > 0 ? (realizado - anoAnterior) / anoAnterior : null,
        ativas,
      },
      resumo: data.resumo && {
        distritosTotal: distritos.length,
        distritosNaMeta: distritos.filter(d => d.pct != null && d.pct >= 1).length,
        gruposComMeta: gruposComMeta.length,
        gruposNaMeta: gruposComMeta.filter(g => g.pct != null && g.pct >= 1).length,
      },
    } as ExecData
  }, [data, regiaoFiltrada, distritosDaRegiao])

  const toggleDist = (cod: string) =>
    setExpandido(prev => { const n = new Set(prev); n.has(cod) ? n.delete(cod) : n.add(cod); return n })

  // Recolhido (por detalhe aberto OU manual): vira uma abinha vertical clicável.
  if (collapsedByDetail || !aberto) {
    return (
      <button onClick={() => setAberto(true)}
        className="absolute top-3 right-3 z-[1000] inline-flex items-center gap-1.5 px-3 py-2 rounded-xl neu-float-2 text-foreground hover:bg-foreground/5 transition-colors text-xs font-semibold"
        title="Abrir painel da diretoria">
        <FiBarChart2 className="w-4 h-4 text-primary" /> Diretoria
        <FiChevronLeft className="w-3 h-3" />
      </button>
    )
  }

  // `vista` = recorte por Estado (ou os dados crus, sem filtro). Só as séries
  // temporais continuam lendo `data`, porque não têm dimensão de distrito.
  const h = vista?.headline
  const camp = data?.campanha
  const modoSemana = data?.modo === 'semana'
  const modoVitrine = data?.modo === 'vitrine'
  const vit = data?.vitrine

  return (
    <div className="absolute top-3 right-3 z-[1000] w-[340px] max-w-[calc(100vw-2rem)] max-h-[calc(100%-24px)] flex flex-col neu-float rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
        <FiBarChart2 className="w-4 h-4 text-primary shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold text-foreground leading-tight">Diretoria</div>
          <div className="text-[10px] text-muted-foreground leading-tight truncate">
            {modoSemana && data?.semana ? `Semana ${semanaLabel(data.semana)}`
              : modoVitrine && vit ? `Vitrine ${vit.numero} · ${semanaLabel(vit.semanaInicial)}–${semanaLabel(vit.semanaFinal)}${vit.emCurso ? ' · em curso' : ''}`
              : camp ? `Campanha ${semanaLabel(camp.semanaInicial)}–${semanaLabel(camp.semanaFinal)}` : ''}
          </div>
        </div>
        <button onClick={() => setMostrarValores(v => !v)}
          title={mask ? 'Mostrar valores em R$ (estão ocultos)' : 'Ocultar valores em R$'}
          className={`p-1 rounded-lg transition-colors hover:bg-foreground/5 ${mask ? 'text-muted-foreground hover:text-foreground' : 'text-primary'}`}>
          {mask ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => carregar(true)} disabled={loading} title="Atualizar (Oracle)"
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-50">
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={() => setAberto(false)} title="Recolher"
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors">
          <FiX className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Escopo geográfico: realizado, meta, ativas, ranking e resumo são somados
          só nos distritos do Estado (cada distrito é de uma UF só). As séries
          temporais não têm dimensão de distrito e seguem da praça — cada uma leva
          seu próprio rótulo no corpo do painel. */}
      {regiaoFiltrada && (
        <div className="px-3 py-1.5 border-b border-border/60 shrink-0 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] leading-snug">
          Recortado em <strong>{regiaoFiltrada}</strong> — evolução semanal e vitrines seguem da praça inteira.
        </div>
      )}

      {/* Seletor de escopo: Semana exige um chip de semana clicado no mapa. */}
      <div className="flex gap-1 px-3 py-1.5 border-b border-border/60 shrink-0">
        {([['semana', 'Semana'], ['vitrine', 'Vitrine'], ['campanha', 'Campanha']] as Array<[Escopo, string]>).map(([k, label]) => {
          const disabled = k === 'semana' && !semanaCodigo
          return (
            <button key={k} onClick={() => setEscopo(k)} disabled={disabled}
              title={disabled ? 'Clique numa semana no painel do mapa pra ver o reporte semanal' :
                k === 'vitrine' ? 'Período comercial de 4 semanas (fecha na última)' :
                k === 'campanha' ? 'Período de objetivos × meta' : 'Só a semana selecionada'}
              className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                escopo === k ? 'bg-primary text-primary-foreground'
                  : disabled ? 'bg-muted/40 text-muted-foreground/40 cursor-not-allowed'
                  : 'bg-muted/40 text-muted-foreground hover:bg-muted'}`}>
              {label}
            </button>
          )
        })}
      </div>

      <div className="overflow-y-auto flex-1 min-h-0">
        {loading && !data && (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground text-xs">
            <FiRefreshCw className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}
        {erro && <div className="m-3 p-2 rounded-lg bg-red-500/10 text-red-600 text-[11px]">{erro}</div>}

        {data && (
          <>
            {!data.oracle.ok && (
              <div className="mx-3 mt-3 p-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] leading-snug">
                {data.oracle.motivo}
              </div>
            )}

            {/* Headline */}
            {h && (
              <div className="px-3 py-3 border-b border-border/60">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                  {modoSemana ? `Realizado na semana ${data?.semana ? semanaLabel(data.semana) : ''}`
                    : modoVitrine ? `Realizado na vitrine ${vit?.numero ?? ''}` : 'Realizado na campanha'}
                </div>
                {/* Vitrine em curso: o destaque é '—' (não fechou — número grande
                    leria como resultado final da vitrine); o parcial vai em linha
                    secundária rotulada. Fechou → volta o número em destaque. */}
                {modoVitrine && vit?.emCurso ? (
                  <>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="text-xl font-bold text-muted-foreground">—</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">ainda não fechou</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Parcial até agora: <span className="font-semibold text-foreground tabular-nums"><Money v={h.realizado} mask={mask} /></span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-bold text-foreground tabular-nums"><Money v={h.realizado} mask={mask} /></span>
                    {h.pct != null && <span className={`text-sm font-bold ${pctColor(h.pct)}`}>{(h.pct * 100).toFixed(0)}%</span>}
                  </div>
                )}
                {/* Barra + meta só no modo campanha (a meta é por período, não por semana) */}
                {h.meta != null && (
                  <>
                    <div className="mt-1.5"><MetaBar pct={h.pct} /></div>
                    <div className="flex items-center justify-between mt-1.5 text-[11px]">
                      <span className="text-muted-foreground">Meta <Money v={h.meta} curto mask={mask} /></span>
                      <span className="text-muted-foreground">vs 2025: <YoY delta={h.deltaYoY} /></span>
                    </div>
                  </>
                )}
                {h.meta == null && !modoVitrine && (
                  <div className="flex items-center justify-between mt-1.5 text-[11px]">
                    <span className="text-muted-foreground">{h.ativas.toLocaleString('pt-BR')} ativas</span>
                    <span className="text-muted-foreground">vs mesma sem. 2025: <YoY delta={h.deltaYoY} /></span>
                  </div>
                )}
                {/* Modo vitrine: dois comparativos "no mesmo ponto" (semanas
                    decorridas) — vitrine anterior e mesma vitrine do ano passado. */}
                {modoVitrine && (
                  <div className="mt-1.5 space-y-0.5 text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">vs vitrine anterior{vit?.emCurso ? ' (mesmo ponto)' : ''}</span>
                      <span className="inline-flex items-center gap-1.5 shrink-0 tabular-nums">
                        {h.anterior != null && <span className="font-semibold text-foreground"><Money v={h.anterior} curto mask={mask} /></span>}
                        <YoY delta={h.deltaPrev ?? null} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">vs mesma vitrine 2025{vit?.emCurso ? ' (mesmo ponto)' : ''}</span>
                      <span className="inline-flex items-center gap-1.5 shrink-0 tabular-nums">
                        {h.anoAnterior > 0 && <span className="font-semibold text-foreground"><Money v={h.anoAnterior} curto mask={mask} /></span>}
                        <YoY delta={h.deltaYoY} />
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  {h.meta != null ? `${h.ativas.toLocaleString('pt-BR')} ativas · ` : ''}
                  {modoVitrine ? `${h.ativas.toLocaleString('pt-BR')} ativas · ${vit?.semanasDecorridas ?? '?'}/${vit?.totalSemanas ?? 4} semanas`
                    : <>{modoSemana ? 'mesma semana 2025 ' : 'ano anterior '}<Money v={h.anoAnterior} curto mask={mask} /></>}
                </div>
                {/* Relógio da campanha — SÓ no modo campanha. "74% da meta" sozinho
                    não diz se está adiantado ou atrasado, e por isso assusta: em
                    19/08 a praça estava em 74% com 3 das 8 semanas por correr, ou
                    seja, folgada. É o par ritmo × precisa que responde isso.
                    Ritmo divide pelas semanas DECORRIDAS (inclui a corrente, ainda
                    parcial, então subestima) e "precisa" divide só pelas que ainda
                    não começaram (então superestima): os dois erram para o lado
                    conservador de propósito. Sai de `h`, que é o headline já
                    recortado por Estado/distribuição — a contagem de semanas não
                    tem dimensão de distrito e vale igual nos dois casos. */}
                {data.modo === 'campanha' && camp?.semanasTotal != null && camp.semanasRestantes != null && (() => {
                  const decorridas = camp.semanasDecorridas ?? 0
                  const ritmo = decorridas > 0 ? h.realizado / decorridas : null
                  const falta = h.meta != null ? h.meta - h.realizado : null
                  const restantes = camp.semanasRestantes!
                  const precisa = falta != null && falta > 0 && restantes > 0 ? falta / restantes : null
                  const noRitmo = ritmo != null && precisa != null && ritmo >= precisa
                  return (
                    <div className="mt-2 rounded-lg bg-muted/40 px-2 py-1.5">
                      <div className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-muted-foreground">
                          {restantes > 0
                            ? <>Faltam <span className="font-bold text-foreground">{restantes}</span> de {camp.semanasTotal} semanas</>
                            : <>Campanha encerrada · {camp.semanasTotal} semanas</>}
                        </span>
                        {falta != null && falta <= 0 && (
                          <span className="font-bold text-emerald-600 dark:text-emerald-400 shrink-0">meta batida</span>
                        )}
                      </div>
                      {(ritmo != null || precisa != null) && (
                        <div className="flex items-center justify-between gap-2 mt-0.5 text-[10px] tabular-nums">
                          {ritmo != null && (
                            <span className="text-muted-foreground">
                              Ritmo <span className="font-semibold text-foreground"><Money v={ritmo} curto mask={mask} /></span>/sem
                            </span>
                          )}
                          {precisa != null && (
                            <span className={`shrink-0 ${noRitmo ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                              Precisa <span className="font-semibold"><Money v={precisa} curto mask={mask} /></span>/sem
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Última semana FECHADA em destaque — SÓ no modo campanha (nos modos
                    semana/vitrine o próprio corpo já mostra as semanas). É o "quanto
                    vendemos na semana X" que a Clara responde. Resolve a confusão de 2026-07-14. */}
                {data.modo === 'campanha' && (() => {
                  const ult = [...data.timeline].reverse().find(t => t.realizado > 0)
                  if (!ult) return null
                  return (
                    <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 px-2 py-1">
                      <span className="text-[10px] text-muted-foreground">Última semana ({semanaLabel(ult.semana)})</span>
                      <span className="text-[11px] font-bold text-foreground tabular-nums"><Money v={ult.realizado} mask={mask} /></span>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Resumo */}
            {vista?.resumo && (
              <div className="flex gap-2 px-3 py-2 border-b border-border/60">
                <div className="flex-1 rounded-lg bg-muted/40 px-2 py-1.5">
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><FiTarget className="w-3 h-3" /> Distritos na meta</div>
                  <div className="text-sm font-bold text-foreground tabular-nums">{vista.resumo.distritosNaMeta}<span className="text-muted-foreground text-xs">/{vista.resumo.distritosTotal}</span></div>
                </div>
                <div className="flex-1 rounded-lg bg-muted/40 px-2 py-1.5">
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><FiAward className="w-3 h-3" /> Grupos na meta</div>
                  <div className="text-sm font-bold text-foreground tabular-nums">{vista.resumo.gruposNaMeta}<span className="text-muted-foreground text-xs">/{vista.resumo.gruposComMeta}</span></div>
                </div>
              </div>
            )}

            {/* Timeline semanal — modos semana/campanha */}
            {!modoVitrine && data.timeline.length > 1 && (
              <div className="px-3 py-2 border-b border-border/60">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Evolução semanal{regiaoFiltrada ? ' · praça inteira' : ''}</div>
                <Sparkline data={data.timeline} mask={mask} />
              </div>
            )}

            {/* Modo vitrine: as semanas da vitrine abertas, fechamento marcado */}
            {modoVitrine && data.timeline.length > 0 && (
              <div className="px-3 py-2 border-b border-border/60">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Semanas da vitrine{regiaoFiltrada ? ' · praça inteira' : ''}</div>
                <div className="grid grid-cols-2 gap-1">
                  {data.timeline.map(t => {
                    const fech = vit != null && t.semanaCodigo === vit.semanaFechamento
                    return (
                      <div key={t.semanaCodigo} className={`rounded-lg px-2 py-1 ${fech ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/40'}`}>
                        <div className="flex items-center justify-between text-[9px] text-muted-foreground">
                          <span>{semanaLabel(t.semana)}</span>
                          {fech && <span className="font-semibold text-primary">{vit?.emCurso ? 'em curso' : 'fechamento'}</span>}
                        </div>
                        <div className="text-[11px] font-bold text-foreground tabular-nums"><Money v={t.realizado} curto mask={mask} /></div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Modo vitrine: fechamentos das últimas vitrines — é onde a tendência
                aparece (semanas comuns × fechamento não se comparam). */}
            {modoVitrine && (data.vitrines?.length ?? 0) > 1 && (
              <div className="px-3 py-2 border-b border-border/60">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Fechamentos por vitrine{regiaoFiltrada ? ' · praça inteira' : ''}</div>
                <div className="flex items-end gap-[3px] h-12">
                  {(() => {
                    // Vitrine em curso não tem fechamento ainda — mostra '—' no
                    // lugar da barra (número parcial passaria por final).
                    const fechadas = data.vitrines!.filter(x => !x.emCurso)
                    const max = Math.max(...fechadas.map(x => x.fechamento), 1)
                    return data.vitrines!.map(v => {
                      const atual = v.periodo === vit?.periodo
                      return (
                        <div key={v.periodo} className="flex-1 flex flex-col items-center gap-0.5 min-w-[10px]">
                          {v.emCurso ? (
                            <span className="text-[10px] leading-none text-muted-foreground pb-0.5"
                              title={`Vitrine ${v.numero}/${Math.floor(v.periodo / 100)} · em curso — ainda não fechou`}>—</span>
                          ) : (
                            <div className={`w-full rounded-sm transition-colors ${atual ? 'bg-primary' : 'bg-primary/40 hover:bg-primary/70'}`}
                              style={{ height: `${Math.max(v.fechamento / max * 40, 2)}px` }}
                              title={mask ? `Vitrine ${v.numero}/${Math.floor(v.periodo / 100)}` :
                                `Vitrine ${v.numero}/${Math.floor(v.periodo / 100)} · fechamento ${brl(v.fechamento, true)} · total ${brl(v.total, true)}`} />
                          )}
                          <span className={`text-[8px] leading-none ${atual ? 'text-primary font-bold' : 'text-muted-foreground'}`}>V{v.numero}</span>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
            )}

            {/* Ranking distritos → grupos */}
            <div className="px-2 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold px-1 mb-1">{modoSemana ? 'Ranking da semana (por venda)' : modoVitrine ? 'Ranking da vitrine (por venda)' : 'Ranking por distrito (% da meta)'}</div>
              {(vista ?? data).distritos.map(d => {
                const exp = expandido.has(d.codigo)
                return (
                  <div key={d.codigo} className="mb-1">
                    <button onClick={() => toggleDist(d.codigo)}
                      className="w-full flex items-center gap-1.5 px-1 py-1 rounded-lg hover:bg-muted/50 transition-colors text-left">
                      {exp ? <FiChevronDown className="w-3 h-3 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-foreground truncate">{d.nome}</span>
                          <span className={`text-[11px] font-bold tabular-nums shrink-0 ${pctColor(d.pct)}`}>{d.pct != null ? `${(d.pct * 100).toFixed(0)}%` : '—'}</span>
                        </div>
                        <div className="mt-0.5"><MetaBar pct={d.pct} /></div>
                        <div className="flex items-center justify-between mt-0.5 text-[9px] text-muted-foreground">
                          <span><Money v={d.realizado} curto mask={mask} />{d.meta ? <> / <Money v={d.meta} curto mask={mask} /></> : ''}</span>
                          <span className="inline-flex items-center gap-1">{d.grupos.length} grupos · <YoY delta={d.deltaYoY} /></span>
                        </div>
                      </div>
                    </button>
                    {exp && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border/60 pl-2">
                        {d.grupos.map(g => (
                          <div key={g.codigo} className="py-0.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] text-foreground truncate">{g.nome}</span>
                              <span className={`text-[10px] font-semibold tabular-nums shrink-0 ${pctColor(g.pct)}`}>{g.pct != null ? `${(g.pct * 100).toFixed(0)}%` : '—'}</span>
                            </div>
                            <div className="flex items-center justify-between text-[9px] text-muted-foreground">
                              <span><Money v={g.realizado} curto mask={mask} />{g.meta ? <> / <Money v={g.meta} curto mask={mask} /></> : ''}</span>
                              <span>{g.ativas} ativas</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {data && (
        <div className="px-3 py-1.5 border-t border-border/60 text-[9px] text-muted-foreground flex items-center justify-between shrink-0">
          <span>Fonte: Oracle DRACO{data.cached ? ' · cache' : ''}</span>
          <span>{data.oracle.ok ? 'ao vivo' : 'MySQL (parcial)'}</span>
        </div>
      )}
    </div>
  )
}

export default ExecutivoPanel
